<script setup>
import { i18n } from 'boot/i18n';
import Banner from 'components/UI/Banner.vue';
import { useDialogPluginComponent } from 'quasar';
import { notifyNegative, notifySuccess, notifyWarning } from 'src/composables/interactions';
import { computed, ref } from 'vue';

import { useActivityReconciliationRunner } from '../composables/useActivityReconciliationRunner';
import ReconciliationSettingsForm from './ReconciliationSettingsForm.vue';
import ReconciliationTable from './ReconciliationTable.vue';

defineOptions({ name: 'ActivityReconciliationDialog' });

const props = defineProps({
  sessionActivities: {
    type: Array,
    required: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const STEPS = {
  CONFIGURATION: 'configuration',
  RESULTS: 'results',
};

const {
  reconciliationTasks, isRunning, isComplete, prepare, match, commit, cancel,
} = useActivityReconciliationRunner();

const numLinkedTasks = ref(0);

const step = ref(STEPS.CONFIGURATION);

const settings = ref({
  startTimeToleranceMinutes: 60,
  endTimeToleranceMinutes: 60,
});

const numSessionActivities = computed(() => props.sessionActivities?.length ?? 0);

const hasSelectedAppointments = computed(() => reconciliationTasks.value.some((task) => task.selectedAppointmentId));

const isFinished = computed(() => step.value !== STEPS.CONFIGURATION && isComplete.value);

/**
 * Start the matching process with the current settings. This will prepare the matching tasks based on
 * the provided session activities and then execute them,
 * @returns {Promise<void>}
 */
function startMatching() {
  prepare(props.sessionActivities);
  step.value = STEPS.RESULTS;
  return match(settings.value);
} // startMatching()

async function commitAppointmentLinks() {
  const commitResult = await commit();

  numLinkedTasks.value += commitResult.linked;

  if (commitResult.failed > 0) {
    notifyNegative(i18n.tc(
      'activityReconciliation.commitResult.failure',
      commitResult.failed,
      { count: commitResult.failed },
    ));
    return;
  }
  if (commitResult.linked > 0) {
    notifySuccess(i18n.tc(
      'activityReconciliation.commitResult.success',
      commitResult.linked,
      { count: commitResult.linked },
    ));
    return;
  }
  notifyWarning(i18n.t('activityReconciliation.commitResult.nothingLinked'));
} // commitAppointmentLinks()

/**
 * Cancel the ongoing matching process. If the matching is currently running, this will
 * signal the matching runner to stop processing any further tasks and will close the dialog.
 */
function closeDialog() {
  if (step.value === STEPS.CONFIGURATION) {
    onDialogCancel();
    return;
  }
  if (isRunning.value) {
    cancel();
  }
  onDialogOK(numLinkedTasks.value);
} // closeDialog()

</script>
<template>
  <q-dialog
    ref="dialogRef"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card class="activity-reconciliation-dialog">
      <q-card-section class="row items-center">
        <div
          class="text-h6 ellipsis col"
        >
          {{ i18n.t('activityReconciliation.dialogTitle') }}
        </div>
        <div class="col-auto">
          <q-btn
            flat
            round
            dense
            icon="close"
            @click="closeDialog"
          />
        </div>
      </q-card-section>
      <q-card-section class="row">
        <banner
          type="info"
          :message="i18n.t('activityReconciliation.description')"
          icon="lightbulb"
        />
      </q-card-section>
      <q-card-section>
        <reconciliation-settings-form
          v-if="step === STEPS.CONFIGURATION"
          v-model:settings="settings"
        />
        <reconciliation-table
          v-else-if="step === STEPS.RESULTS"
          :reconciliation-tasks="reconciliationTasks"
          :is-running="isRunning"
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn
          v-if="isFinished"
          flat
          :label="i18n.t('common.buttonClose')"
          color="primary"
          @click="closeDialog"
        />
        <template v-else>
          <q-btn
            flat
            :label="i18n.t('common.buttonCancel')"
            color="primary"
            @click="closeDialog"
          />
          <q-btn
            v-if="step === STEPS.CONFIGURATION"
            flat
            :label="i18n.t('activityReconciliation.buttonReviewActivities')"
            :disable="numSessionActivities === 0 || isRunning"
            :loading="isRunning"
            color="primary"
            @click="startMatching"
          />
          <q-btn
            v-else-if="step === STEPS.RESULTS"
            flat
            :label="i18n.t('activityReconciliation.buttonLinkAppointments')"
            :disable="!hasSelectedAppointments || isRunning"
            :loading="isRunning"
            color="primary"
            @click="commitAppointmentLinks"
          />
        </template>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style scoped>
.activity-reconciliation-dialog {
  min-width:80vw;
}
</style>
